import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from '@reduxjs/toolkit';
import { CardType } from 'utils/customTypes';
import { RootState, ThunkApi } from 'state/store';
import { Status } from 'utils/customTypes';
import { SLICE_STATUS } from 'utils/constants';

/* ============================== STATE SETUP =============================== */
interface CardTypeState {
  status: Status;
  total: number;
}

const initialState: CardTypeState = {
  status: SLICE_STATUS.IDLE,
  total: 0,
};

const cardTypeAdapter = createEntityAdapter<CardType>();

/* ============================== REDUX THUNK =============================== */
function concurrencyCondition(
  thunkParameters: unknown,
  { getState }: { getState: () => RootState }
): boolean {
  const { status } = getState().normalized.cardType;
  return status !== SLICE_STATUS.LOADING;
}

export const getCardTypes = createAsyncThunk<
  {
    data: CardType[];
    totalCount: number;
  },
  void,
  ThunkApi
>(
  'normalized/cardTypes/getCardTypes',
  async (_, { extra: { getCardTypeApi } }) => {
    const data = await getCardTypeApi().getList();
    return { data, totalCount: data.length };
  },
  { condition: concurrencyCondition }
);

/* ================================= REDUCER ================================ */
const cardTypeSlice = createSlice({
  name: 'normalized/cardTypes',
  initialState: cardTypeAdapter.getInitialState(initialState),
  reducers: {
    clearAllCardTypes(state) {
      cardTypeAdapter.removeAll(state);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCardTypes.pending, (state) => {
        state.status = SLICE_STATUS.LOADING;
      })
      .addCase(getCardTypes.rejected, (state) => {
        state.status = SLICE_STATUS.FAILED;
      })
      .addCase(getCardTypes.fulfilled, (state, action) => {
        cardTypeAdapter.upsertMany(state, action.payload.data);
        state.ids = Array.from(
          new Set([
            ...state.ids,
            ...action.payload.data.map((dashboard) => dashboard.id),
          ])
        );

        state.total = action.payload.totalCount;
        state.status = SLICE_STATUS.SUCCESS;
      });
  },
});

/* ================================ ACTIONS ================================= */
export const { clearAllCardTypes } = cardTypeSlice.actions;

/* =============================== SELECTORS ================================ */
const defaultSelectors = cardTypeAdapter.getSelectors<RootState>(
  (state) => state.normalized.cardType
);

const selectTotalCardTypes = (state: RootState) =>
  state.normalized.cardType.total;

const selectCardTypeById = (state: RootState, id: string) =>
  cardTypeSelectors.selectById(state, id);

const selectCardTypesById = (state: RootState, ids: string[]) => {
  const cardTypes = cardTypeSelectors.selectAll(state);
  return cardTypes.filter((cardType) => ids.includes(cardType.id));
};

const selectCardNameByInsightId = (
  state: RootState,
  insightId: string
): string => {
  const cardTypes = cardTypeSelectors.selectAll(state);
  const cardType = cardTypes.find(
    (cardType) => cardType.insightId === insightId
  );
  return cardType?.name || '';
};

export const cardTypeSelectors = {
  ...defaultSelectors,
  selectTotalCardTypes,
  selectCardTypeById,
  selectCardTypesById,
  selectCardNameByInsightId,
};

export default cardTypeSlice.reducer;
